import React, { useEffect } from 'react';

import { useIntl } from 'react-intl-next';

import Lozenge from '@atlaskit/lozenge';
import Select from '@atlaskit/select';
import { Flex } from '@atlassian/eoc-flex';

import commonMessages from '../../../common/messages';
import { type ElseSelectOptionType } from '../../../common/types';
import { OptionLabel } from '../../../common/ui/styled';
import { useRoutingRulesController } from '../../../controllers/routing-rules';

import messages from './messages';
import {
	DeletedLozengeWrapper,
	ElseRow,
	ElseRuleCardContentContainer,
	SelectContainer,
} from './styled';
import { type ElseCardContentProps } from './types';

export const ElseCardContent: React.FC<ElseCardContentProps> = ({
	entityCount,
	entity,
	routeToOptions,
	isDeleted,
	hasEditRight,
}) => {
	const { formatMessage } = useIntl();

	const { handleChangeLastRule, isUpdating, lastRuleValue, setLastRuleValue } =
		useRoutingRulesController({});

	useEffect(() => {
		if (routeToOptions) {
			if (isDeleted) {
				setLastRuleValue(null);
				return;
			}

			const options = routeToOptions.flatMap((option) => option.options);

			const defaultValue = options.find((option) => {
				if (option?.value === entity.entityId) {
					return option?.value === entity.entityId;
				}
			});

			const noOneValue = options.find((option) => option?.value === 'noOne');

			setLastRuleValue(defaultValue || noOneValue);
		}
	}, [entity.entityId, routeToOptions, setLastRuleValue, isDeleted]);

	const handleChange = (selectedItem: ElseSelectOptionType | null) => {
		if (selectedItem) {
			handleChangeLastRule(entity, selectedItem);
		}
	};

	return (
		<ElseRuleCardContentContainer isLastItem>
			<Flex alignItems="center" columnGap={3.75} flex={1}>
				{entityCount > 1 && (
					<Lozenge appearance="inprogress">{formatMessage(messages.else)}</Lozenge>
				)}
				<ElseRow>
					<Flex alignItems="center" columnGap={3} flex={1}>
						<div id="route-all-alerts-to">
							{formatMessage(
								entityCount > 1 ? messages.routeAllAlertsTo : messages.routeAllAlertsToSingle,
							)}
						</div>
						<SelectContainer>
							<Select
								placeholder={isDeleted ? formatMessage(messages.deletedEntity) : undefined}
								options={routeToOptions}
								value={lastRuleValue}
								onChange={handleChange}
								isLoading={isUpdating}
								isDisabled={isUpdating || !hasEditRight}
								formatOptionLabel={(option) => (
									<Flex justifyContent="space-between" alignItems="center" columnGap={1}>
										<OptionLabel>{option?.label}</OptionLabel>
										<Flex columnGap={1}>
											{option?.enabled === false && (
												<Lozenge appearance="moved">{formatMessage(commonMessages.off)}</Lozenge>
											)}
										</Flex>
									</Flex>
								)}
								labelId="route-all-alerts-to"
							/>
						</SelectContainer>
						{isDeleted && (
							<DeletedLozengeWrapper>
								<Lozenge appearance="removed">{formatMessage(messages.routeTargetDeleted)}</Lozenge>
							</DeletedLozengeWrapper>
						)}
					</Flex>
				</ElseRow>
			</Flex>
		</ElseRuleCardContentContainer>
	);
};
